/** Related Assets zone for Generate Image — structured input context below Result.
 * Maps existing Canvas `inputMaterials` / edges; detach only (never deletes library assets).
 *
 * VistoeraTASK-00144 — the section's own open/closed state is now
 * CONTROLLED by the parent (`GenerateImageNode.tsx`'s local,
 * non-persisted `relatedAssetsOpen`) instead of an internal `useState`,
 * because the parent must know whether each chip's REAL one-to-one
 * `<Handle>` is currently mounted here (to decide whether to render its
 * OWN legacy-hidden fallback anchor instead — §三/§四). `imageInputHandleId`
 * is threaded straight through to `InputMaterialsRow` so each IMAGE chip
 * can mount its own stable, edge-id-keyed anchor.
 *
 * VistoeraTASK-00574 — 「+ 添加」 is a real button that opens the Image
 * Asset Picker (parent supplies `onAddClick`); previously it was a
 * non-interactive `<span>` with only a drag/connect tooltip. */

import { DownOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons'
import { Handle, Position } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import type { InputMaterial } from '../dataflow'
import { RELATED_ASSET_SLOT_AGGREGATE_POSITION } from '../generateImageInputHandleVisibility'
import InputMaterialsRow from './InputMaterialsRow'
import VideoInputSectionHandle from './VideoInputSectionHandle'

interface Props {
  workspaceId: string
  materials: InputMaterial[]
  open: boolean
  onToggleOpen: () => void
  /** Expanded Generate Image node — section owns the aggregate `image-ref-in` handle. */
  nodeExpanded?: boolean
  aggregateInputHandleId?: string
  showSectionAggregateHandle?: boolean
  dropZoneClassName?: string
  /** VistoeraTASK-00341 — per-edge target ids stacked at slot left-center (not on thumbnails). */
  stackedPerEdgeTargetHandleIds?: string[]
  onDisconnect: (edgeId: string) => void
  onSelectSource: (nodeId: string) => void
  onEditText: (nodeId: string) => void
  /** VistoeraTASK-00574 — open the Image Asset Picker to add references. */
  onAddClick?: () => void
  addDisabled?: boolean
}

export default function ImageRelatedAssetsSection({
  workspaceId,
  materials,
  open,
  onToggleOpen,
  nodeExpanded = false,
  aggregateInputHandleId,
  showSectionAggregateHandle = false,
  dropZoneClassName = '',
  stackedPerEdgeTargetHandleIds = [],
  onDisconnect,
  onSelectSource,
  onEditText,
  onAddClick,
  addDisabled = false,
}: Props) {
  const { t } = useTranslation('canvas')
  const count = materials.length
  const dropZoneActive = nodeExpanded && showSectionAggregateHandle

  return (
    <div
      className={`pcv-gen-related-assets pcv-brief-section${dropZoneActive ? ' pcv-gen-related-assets-drop-zone' : ''}${dropZoneClassName}`}
      data-testid="image-related-assets"
      data-expanded-input-zone={dropZoneActive ? 'yes' : 'no'}
    >
      {dropZoneActive && aggregateInputHandleId ? (
        <div
          className="pcv-gen-related-assets-slot-handle-stack"
          data-testid="image-related-assets-slot-handle-stack"
          data-aggregate-position={RELATED_ASSET_SLOT_AGGREGATE_POSITION}
        >
          <VideoInputSectionHandle
            id={aggregateInputHandleId}
            className="pcv-handle pcv-handle--structured-asset pcv-dropzone-handle"
            testId="image-expanded-aggregate-in-handle"
          />
          {stackedPerEdgeTargetHandleIds.map((handleId) => (
            <Handle
              key={`slot-stack::${handleId}`}
              type="target"
              position={Position.Left}
              id={handleId}
              className="pcv-handle pcv-handle--structured-asset pcv-handle--legacy-hidden pcv-handle--slot-stacked"
            />
          ))}
        </div>
      ) : null}
      <div className="pcv-gen-related-assets-header">
        <button
          type="button"
          className="pcv-gen-related-assets-toggle nodrag"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation()
            onToggleOpen()
          }}
        >
          {open ? <DownOutlined aria-hidden /> : <RightOutlined aria-hidden />}
          <span className="pcv-brief-section-label">{t('generation.relatedAssets.label')}</span>
          <span className="pcv-gen-related-assets-count">{t('generation.relatedAssets.count', { count })}</span>
        </button>
        {onAddClick ? (
          <button
            type="button"
            className="pcv-brief-asset-add-btn pcv-gen-related-assets-add-btn nodrag"
            data-testid="image-related-assets-add"
            disabled={addDisabled}
            title={t('generation.relatedAssets.addHint')}
            onClick={(e) => {
              e.stopPropagation()
              onAddClick()
            }}
          >
            <PlusOutlined aria-hidden />
            {t('generation.relatedAssets.add')}
          </button>
        ) : (
          <span className="pcv-gen-related-assets-add-hint" title={t('generation.relatedAssets.addHint')}>
            <PlusOutlined aria-hidden />
            {t('generation.relatedAssets.add')}
          </span>
        )}
      </div>

      {open ? (
        count > 0 ? (
          <InputMaterialsRow
            workspaceId={workspaceId}
            materials={materials}
            onDisconnect={onDisconnect}
            onSelectSource={onSelectSource}
            onEditText={onEditText}
            draggableMentions
            hideLabel
          />
        ) : (
          <div className="pcv-gen-related-assets-empty" data-testid="image-related-assets-empty">
            {t('generation.relatedAssets.emptyHint')}
          </div>
        )
      ) : dropZoneActive ? (
        <div className="pcv-gen-related-assets-collapsed-drop-hint" data-testid="image-related-assets-collapsed-drop-hint">
          {t('generation.relatedAssets.emptyHint')}
        </div>
      ) : null}
    </div>
  )
}
